import { Data, Effect } from 'effect';

/** The subset of Clerk's getToken options callers may pass through the bridge. */
export type ClerkTokenOptions = { skipCache?: boolean };

export type ClerkTokenGetter = (options?: ClerkTokenOptions) => Promise<string | null>;

let tokenGetter: ClerkTokenGetter | null = null;

export class ClerkTokenBridgeError extends Data.TaggedError('ClerkTokenBridgeError')<{
  readonly operation: 'get';
}> {}

const clerkTokenCauses = new WeakMap<ClerkTokenBridgeError, unknown>();

function clerkTokenFailure(cause: unknown): ClerkTokenBridgeError {
  const error = new ClerkTokenBridgeError({ operation: 'get' });
  clerkTokenCauses.set(error, cause);
  return error;
}

export function clerkTokenBridgeErrorCause(error: ClerkTokenBridgeError): unknown {
  return clerkTokenCauses.get(error);
}

export function restoreClerkTokenBridgeError<A, R>(
  program: Effect.Effect<A, ClerkTokenBridgeError, R>,
): Effect.Effect<A, unknown, R> {
  return program.pipe(Effect.catch((error) => Effect.fail(clerkTokenBridgeErrorCause(error) ?? error)));
}

export function setClerkTokenGetter(fn: ClerkTokenGetter | null) {
  tokenGetter = fn;
}

/**
 * Only `skipCache: true` is forwarded. getClerkToken is also handed around as
 * a bare function reference (Effect.tryPromise calls it with an AbortSignal),
 * and those callers must keep reading the cached token with a zero-argument
 * getter call.
 */
function forwardedTokenOptions(options?: ClerkTokenOptions): ClerkTokenOptions | undefined {
  return options?.skipCache === true ? { skipCache: true } : undefined;
}

export const getClerkTokenEffect = Effect.fn('web.clerk.getToken')(function* (
  options?: ClerkTokenOptions,
) {
  const forwarded = forwardedTokenOptions(options);
  return yield* Effect.tryPromise({
    try: () => {
      if (!tokenGetter) return Promise.resolve(null);
      return forwarded ? tokenGetter(forwarded) : tokenGetter();
    },
    catch: clerkTokenFailure,
  });
});

export async function getClerkToken(options?: ClerkTokenOptions): Promise<string | null> {
  return Effect.runPromise(restoreClerkTokenBridgeError(getClerkTokenEffect(options)));
}
